import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
} from '@epicuro-next/platform/configuration';
import {
  DeliveryItem,
  MemoPrepack,
} from '@epicuro-next/state/config-strip-state';
import { OrderType, PreviewItem } from 'graphql-api/gql-api-interfaces';

import { PreviewSizeGridComponentStore } from './preview-size-grid.component-store';

interface SizeQuantityMap {
  [productId: string]: DeliveryItem;
}

@Component({
  selector: 'epc-preview-product-sizes-v2',
  templateUrl: './preview-product-sizes-v2.component.html',
  styleUrls: ['./preview-product-sizes-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PreviewSizeGridComponentStore],
  standalone: false,
})
export class PreviewProductSizesComponentV2 implements OnInit {
  private readonly componentStore = inject(PreviewSizeGridComponentStore);

  public articleId = input<string>('');

  public brandId = input<string>('');
  public loadPrepacks = input<boolean>(false);

  public previewItems = input<PreviewItem[]>([]);

  // Prepacks can be provided through input or can be loaded through api (use loadPrepacks input)
  public prepacks = input<MemoPrepack[]>([]);

  public selectCached = toSignal(this.componentStore.cachedProduct$);

  private sizesPrepacks = computed(() => {
    const allPrepacks = (this.selectCached()?.prepacks ?? []).concat(
      this.prepacks() ?? [],
    );
    const [anyPreviewItem] = this.previewItems();
    const quantities =
      allPrepacks?.find(({ productId }) => productId === anyPreviewItem.itemId)
        ?.quantities || [];

    return this.previewItems().reduce((all: SizeQuantityMap, previewItem) => {
      return quantities?.reduce(
        (acc: SizeQuantityMap, item) => ({
          ...acc,
          [item.productId]: {
            quantity:
              item.quantity *
                (previewItem.confirmedQuantity ??
                  previewItem.requestedQuantity) +
              (all[item.productId]?.quantity ?? 0),
          },
        }),
        {},
      );
    }, {} as SizeQuantityMap);
  });

  private readonly sizesMaterials = computed(() => {
    return this.previewItems().reduce(
      (acc, previewItem) => ({
        ...acc,
        [previewItem.itemId]: {
          quantity:
            (acc[previewItem.itemId]?.quantity || 0) +
            (previewItem.confirmedQuantity ?? previewItem.requestedQuantity),
        },
      }),
      {} as SizeQuantityMap,
    );
  });

  public materialsWithSizes = computed(() => {
    const allSizeIds = this.allSizes();
    return (this.selectCached()?.materials ?? []).filter(({ children }) =>
      children?.find(({ productId }) => !!allSizeIds[productId]),
    );
  });

  private allSizes = computed(() => {
    return {
      ...this.sizesMaterials(),
      ...this.sizesPrepacks(),
    };
  });

  public sizesMerged = computed(() => {
    return {
      orderType: OrderType.Now,
      items: this.allSizes(),
    };
  });

  public differentMaterialDimensions = toSignal(
    this.componentStore.differentMaterialDimensions$,
    {
      initialValue:
        configStripConfigurationDefaultValues[
          ConfigStripConfigurationKeys.DifferentMaterialDimensions
        ],
    },
  );

  public readonly productLoading = toSignal(
    this.componentStore.isLoadingProduct$,
  );

  public ngOnInit() {
    this.componentStore.setProduct({
      articleId: this.articleId(),
      brandId: this.brandId(),
      loadPrepacks: this.loadPrepacks(),
    });
  }
}
